/**
 * Guide Profile Validation Utilities
 *
 * Validation rules for guide signup flow
 * Epic: MVP 1.2.1 - Guide Signup Flow
 * Location: src/utils/guideValidation.js
 */

import { isValidEmail, isValidPhone, isValidDayRate } from './formHelpers';

/**
 * Validate Stage 1: Core Identity
 * @param {Object} data - Form data
 * @returns {Object} { isValid: boolean, errors: Object }
 */
export const validateStage1 = (data) => {
  const errors = {};

  // First name
  if (!data.firstName || data.firstName.trim().length < 2) {
    errors.firstName = 'First name must be at least 2 characters';
  }

  // Last name
  if (!data.lastName || data.lastName.trim().length < 2) {
    errors.lastName = 'Last name must be at least 2 characters';
  }

  // Email
  if (!data.email) {
    errors.email = 'Email is required';
  } else if (!isValidEmail(data.email)) {
    errors.email = 'Please enter a valid email address';
  }

  // Password (if not using OAuth)
  if (!data.isOAuth) {
    if (!data.password) {
      errors.password = 'Password is required';
    } else if (data.password.length < 8) {
      errors.password = 'Password must be at least 8 characters';
    }
  }

  // Business Name
  if (!data.businessName || data.businessName.trim().length < 2) {
    errors.businessName = 'Business name is required';
  }

  // Phone
  if (!data.phone) {
    errors.phone = 'Phone number is required';
  } else if (!isValidPhone(data.phone)) {
    errors.phone = 'Please enter a valid 10-digit phone number';
  }

  // Location
  if (!data.location || data.location.trim().length < 2) {
    errors.location = 'Location is required';
  }

  // Terms & Conditions
  if (!data.termsAccepted) {
    errors.termsAccepted = 'You must accept the terms and conditions to continue';
  }

  // Profile Photo - Optional, so no validation required

  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};

/**
 * Validate Stage 2: Service Offering
 * @param {Object} data - Form data
 * @returns {Object} { isValid: boolean, errors: Object }
 */
export const validateStage2 = (data) => {
  const errors = {};

  // Service Description
  if (!data.serviceDescription || data.serviceDescription.trim().length < 50) {
    errors.serviceDescription = 'Service description must be at least 50 characters';
  } else if (data.serviceDescription.trim().length > 500) {
    errors.serviceDescription = 'Service description must be no more than 500 characters';
  }

  // Primary Activity
  if (!data.primaryActivity) {
    errors.primaryActivity = 'Please select your primary activity';
  }

  // Day Rate
  if (!data.dayRateCents) {
    errors.dayRate = 'Day rate is required';
  } else if (!isValidDayRate(data.dayRateCents)) {
    errors.dayRate = 'Day rate must be between $100 and $2,000';
  }

  // Experience Range
  if (!data.experienceRange) {
    errors.experienceRange = 'Please select your experience range';
  }

  // Availability Estimate
  if (!data.availabilityEstimate) {
    errors.availabilityEstimate = 'Please select your availability estimate';
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};

/**
 * Validate Stage 3: Profile Enhancement
 * @param {Object} data - Form data
 * @returns {Object} { isValid: boolean, errors: Object }
 */
export const validateStage3 = (data) => {
  const errors = {};

  // Bio (optional)
  if (data.bio && data.bio.length > 2000) {
    errors.bio = 'Bio must be no more than 2,000 characters';
  }

  // All fields in Stage 3 are optional
  // Profile photo, bio, and gallery photos are all optional enhancements

  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};

/**
 * Validate complete profile
 * @param {Object} data - Complete profile data
 * @returns {Object} { isValid: boolean, errors: Object }
 */
export const validateCompleteProfile = (data) => {
  const stage1Validation = validateStage1(data);
  const stage2Validation = validateStage2(data);
  const stage3Validation = validateStage3(data);

  return {
    isValid: stage1Validation.isValid && stage2Validation.isValid && stage3Validation.isValid,
    errors: {
      ...stage1Validation.errors,
      ...stage2Validation.errors,
      ...stage3Validation.errors
    }
  };
};

/**
 * Check if stage is complete (no blockers)
 * @param {number} stage - Stage number (1-3)
 * @param {Object} data - Form data
 * @returns {boolean} True if stage is complete
 */
export const isStageComplete = (stage, data) => {
  switch (stage) {
    case 1:
      return validateStage1(data).isValid;
    case 2:
      return validateStage2(data).isValid;
    case 3:
      return validateStage3(data).isValid;
    default:
      return false;
  }
};

/**
 * Get completion percentage for profile
 * @param {Object} data - Profile data
 * @returns {number} Completion percentage (0-100)
 */
export const getProfileCompletionPercentage = (data) => {
  const requiredFields = [
    'firstName',
    'lastName',
    'email',
    'phone',
    'profilePhotoUrl',
    'serviceDescription',
    'primaryActivity',
    'dayRateCents',
    'experienceRange',
    'availabilityEstimate',
    'serviceAreaLat',
    'serviceAreaLng'
  ];

  const completedFields = requiredFields.filter(field => {
    const value = data[field];
    return value !== null && value !== undefined && value !== '';
  });

  return Math.round((completedFields.length / requiredFields.length) * 100);
};
